/** Tiny typed DOM builders. All text goes through createTextNode — no HTML parsing,
 * so CSV-derived strings can never become markup. */

export type Child = Node | string | number | null | undefined | false;

export interface Attrs {
  class?: string;
  title?: string;
  hidden?: boolean;
  type?: string;
  accept?: string;
  tabIndex?: number;
  role?: string;
  href?: string;
  dataset?: Record<string, string>;
  aria?: Record<string, string>;
  onClick?: (e: MouseEvent) => void;
}

function applyChildren(node: Element, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs.class) node.className = attrs.class;
  if (attrs.title) node.title = attrs.title;
  if (attrs.hidden) node.hidden = true;
  if (attrs.type && 'type' in node) (node as HTMLInputElement).type = attrs.type;
  if (attrs.accept && node instanceof HTMLInputElement) node.accept = attrs.accept;
  if (attrs.tabIndex !== undefined) node.tabIndex = attrs.tabIndex;
  if (attrs.role) node.setAttribute('role', attrs.role);
  if (attrs.href && node instanceof HTMLAnchorElement) node.href = attrs.href;
  if (attrs.dataset) for (const [k, v] of Object.entries(attrs.dataset)) node.dataset[k] = v;
  if (attrs.aria) for (const [k, v] of Object.entries(attrs.aria)) node.setAttribute(`aria-${k}`, v);
  if (attrs.onClick) node.addEventListener('click', attrs.onClick as EventListener);
  applyChildren(node, children);
  return node;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export function svg(
  tag: string,
  attrs: Record<string, string> = {},
  ...children: Child[]
): SVGElement {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  applyChildren(node, children);
  return node;
}
